// Single source of truth for everything a crawler reads: per-route metadata, the
// sitemap, and the JSON-LD graph. The app applies the per-route half at runtime;
// scripts/inject-seo.mjs bakes the rest into build/index.html so it is present in
// the raw HTML for crawlers that do not run JavaScript.

import {
  CONTACT_EMAIL,
  GAME_NAME,
  SITE_URL,
  SOCIAL_LINKS,
  STEAM_URL,
  STUDIO_NAME,
  STUDIO_SHORT_NAME,
  TAGLINE,
} from './site-config.mjs';
import { FAQ_ITEMS } from './pages/home/faq-content.mjs';

export const absoluteUrl = (path = '/') =>
  `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`;

export const OG_IMAGE = absoluteUrl('/og.jpg');
export const OG_IMAGE_ALT =
  'A survivor and a wolf facing each other across a campfire in the co-op survival game Warm.';

const TRAILER_URL = absoluteUrl('/media/warm-trailer.mp4');
// Google rejects a date-only uploadDate and warns when the offset is missing, so
// this has to stay a full ISO 8601 timestamp including the timezone.
const TRAILER_UPLOAD_DATE = '2026-08-10T09:00:00-05:00';

export const GAME_DESCRIPTION =
  'Warm is a co-op winter survival game for PC. Keep one fire alive through nights that get worse. Solo or 1-4 players on Steam.';

const INDEXABLE = 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';

// Files that change every page, so a change to any of them legitimately moves
// every sitemap lastmod.
const SHARED_SOURCES = [
  'src/App.js',
  'src/index.js',
  'src/components',
  'src/styles',
  'src/seo.mjs',
  'src/site-config.mjs',
];

/**
 * Titles lead with the game name and the phrase people actually search for.
 * Descriptions are written to sit under 160 characters so they survive intact
 * in a result snippet.
 *
 * `sources` are the paths whose last commit date becomes the sitemap lastmod.
 * Google ignores lastmod values it decides are untrustworthy, so it has to
 * reflect the content rather than the time of the build.
 */
export const PAGE_SEO = {
  '/': {
    title: `${GAME_NAME} - Co-op Winter Survival Game on Steam`,
    description: GAME_DESCRIPTION,
    robots: INDEXABLE,
    sources: ['src/pages/home', 'public/media'],
  },
  '/feedback': {
    title: `Send Feedback - ${GAME_NAME}`,
    description: `Got a bug or an idea for ${GAME_NAME}? Send it here. A screenshot helps if the problem is something you can see.`,
    robots: INDEXABLE,
    sources: ['src/pages/feedback'],
  },
  '/privacy': {
    title: `Privacy Policy - ${STUDIO_SHORT_NAME}`,
    description: `How ${STUDIO_NAME} handles the data collected by ${GAME_NAME} and this website.`,
    robots: INDEXABLE,
    sources: ['src/pages/legal'],
  },
  '/terms': {
    title: `Terms and Conditions - ${STUDIO_SHORT_NAME}`,
    description: `The terms that apply when you use ${STUDIO_NAME} websites and games.`,
    robots: INDEXABLE,
    sources: ['src/pages/legal'],
  },
  '/eula': {
    title: `End User License Agreement - ${STUDIO_SHORT_NAME}`,
    description: `The licence terms that cover installing and playing ${GAME_NAME}.`,
    robots: INDEXABLE,
    sources: ['src/pages/legal'],
  },
};

export const sourcesFor = (route) => [...SHARED_SOURCES, ...(PAGE_SEO[route].sources || [])];

const NOT_FOUND_SEO = {
  title: `Page not found - ${GAME_NAME}`,
  description: 'That page is not here. Head back to the fire.',
  // A single page app cannot answer with a 404 status, so the noindex is what
  // keeps unknown URLs out of the index instead of piling up as soft 404s.
  robots: 'noindex, follow',
};

export const getPageSeo = (pathname) => PAGE_SEO[pathname] || NOT_FOUND_SEO;

export const SITEMAP_PATHS = Object.keys(PAGE_SEO);

/**
 * @param assets Absolute URLs for bundled images, which carry a content hash
 *   that only exists after a build. Omitted during development.
 */
export function buildStructuredData({ screenshots = [], trailerPoster } = {}) {
  const studioId = `${SITE_URL}/#studio`;
  const gameId = `${SITE_URL}/#game`;

  const trailer = {
    '@type': 'VideoObject',
    '@id': `${SITE_URL}/#trailer`,
    name: `${GAME_NAME} trailer`,
    description: `The trailer for ${GAME_NAME}. Fire, snow, wolves.`,
    contentUrl: TRAILER_URL,
    uploadDate: TRAILER_UPLOAD_DATE,
    duration: 'PT30S',
    thumbnailUrl: [trailerPoster || OG_IMAGE],
    publisher: { '@id': studioId },
  };

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': studioId,
        name: STUDIO_NAME,
        alternateName: STUDIO_SHORT_NAME,
        url: SITE_URL,
        email: CONTACT_EMAIL,
        logo: absoluteUrl('/icons/icon-512x512.png'),
        sameAs: SOCIAL_LINKS.map(({ url }) => url),
      },
      {
        '@type': 'WebSite',
        '@id': `${SITE_URL}/#website`,
        url: SITE_URL,
        name: `${STUDIO_SHORT_NAME} - ${GAME_NAME}`,
        description: GAME_DESCRIPTION,
        publisher: { '@id': studioId },
        inLanguage: 'en-US',
      },
      {
        '@type': 'VideoGame',
        '@id': gameId,
        name: GAME_NAME,
        alternateName: `${GAME_NAME}: ${TAGLINE}`,
        url: SITE_URL,
        description: GAME_DESCRIPTION,
        slogan: TAGLINE,
        image: OG_IMAGE,
        ...(screenshots.length ? { screenshot: screenshots } : {}),
        trailer,
        genre: ['Survival', 'Survival horror', 'Co-op', 'Adventure', 'Indie'],
        applicationCategory: 'Game',
        gamePlatform: ['PC', 'Microsoft Windows'],
        operatingSystem: 'Windows',
        playMode: ['SinglePlayer', 'CoOp'],
        numberOfPlayers: { '@type': 'QuantitativeValue', minValue: 1, maxValue: 4 },
        inLanguage: 'en',
        author: { '@id': studioId },
        publisher: { '@id': studioId },
        // The Steam page is the same entity as this one, which is what lets a
        // search engine treat the two listings as one game rather than two.
        sameAs: [STEAM_URL, ...SOCIAL_LINKS.map(({ url }) => url)],
      },
      {
        '@type': 'FAQPage',
        '@id': `${SITE_URL}/#faq`,
        about: { '@id': gameId },
        mainEntity: FAQ_ITEMS.map(({ question, answer }) => ({
          '@type': 'Question',
          name: question,
          acceptedAnswer: { '@type': 'Answer', text: answer },
        })),
      },
    ],
  };
}
